import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import styles from './ScormTrainingContent.module.scss';
import { clearState, completePortalTraining, fetchScormTrainingInfo,
  fetchTrainingDetail, getScormContent, saveScormContentInfo } from '../redux/action';
import HeaderBack from '../../../components/headerBack';
import Loading from '../../../components/loading';
import { PORTAL } from '../../../constants/RouterConstants';

const ScormTrainingContent = ({
  actionFetchTraining,
  actionFetchScormContent,
  actionClearState,
  scormContent,
  trainingScormContentAction,
  actionFetchScormTrainingDraft,
  trainingFetchAction,
  fetchScormContetAction,
  actionSaveScormContentInfo,
  scormTrainingDraft,
  actionCompleteTraining,
  trainingCompletion,
  history,
  match: { params },
  trainingPermission
}) => {
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [scormUrl, setScormUrl] = useState('');
  const iframeRef = useRef(null);
  const [scormData, setScormData] = useState({});
  const [scormVersion, setScormVersion] = useState('');
  const id = params.trainingId;
  const completionTriggered = useRef(false);

  useEffect(() => {
    if (id) {
      setLoadingStatus(true);
      actionFetchTraining(id);
      actionFetchScormContent(id);
      actionFetchScormTrainingDraft(id);
    }

    return () => {
      actionClearState();
      setScormData({});
      setScormUrl('');
      setScormVersion('');
      setLoadingStatus(false);
    };
  }, [id, actionFetchTraining, actionFetchScormContent, actionClearState, actionFetchScormTrainingDraft]);

  useEffect(() => {
    const { success: scormSuccess } = trainingScormContentAction || {};
    const { success: fetchScormSuccess } = fetchScormContetAction || {};

    if (scormSuccess && trainingFetchAction?.success && fetchScormSuccess) {
      setLoadingStatus(false);
    }

    if (scormContent?.url && scormContent?.url !== scormUrl) {
      setScormUrl(scormContent?.url);
      const version = scormContent?.scorm_version?.toString();
      setScormVersion(version?.includes('2004') ? '2004' : version);
    }

    if (fetchScormSuccess && scormTrainingDraft) {
      const cmiKeys = Object.keys(scormTrainingDraft?.tracking_info || {})
        .filter(key => key.startsWith('cmi') && scormTrainingDraft?.tracking_info[key] != null);

      const cmiObj = cmiKeys.reduce((acc, key) => ({ ...acc, [key]: scormTrainingDraft.tracking_info[key] }), {});

      setScormData(cmiObj);
    }
  }, [trainingScormContentAction, trainingFetchAction, fetchScormContetAction, scormContent, scormUrl, scormTrainingDraft]);

  const isScormVersion = (version, type) => ((version === '1.1' || version === '1.2')
    ? type === 'cmi.core.lesson_status' : type === 'cmi.completion_status' || type === 'cmi.success_status' || type === 'cmi.location');

  const handleMessage = useCallback((event) => {
    const status = ['passed', 'completed', 'failed'];
    if (event.data.type === 'SCORM_SET_VALUE') {
      const { element, value } = event.data.data;
      const payload = {
        tracking_info: { [element]: value }
      };
      if (completionTriggered.current) return;
      if (scormVersion === '2004' && scormData['cmi.location'] !== undefined) {
        payload.level_completion = { 'cmi.location': scormData['cmi.location'] };
      }

      if (value && !['cmi.core.session_time', 'cmi.session_time'].includes(element)) {
        updateScormData(element, value);
        actionSaveScormContentInfo(id, payload);
      } else if (value) {
        updateScormData(element, value);
        actionSaveScormContentInfo(id, payload);
      }

      if (!completionTriggered.current) {
        if (isScormVersion(scormVersion, element) && status.includes(value) && scormTrainingDraft) {
          const data = { status: value === 'failed' ? 'fail' : value };
          completionTriggered.current = true;
          actionCompleteTraining(scormTrainingDraft.id, data);
        } else if (isScormVersion(scormVersion, element) && ['finish'].includes(value) && scormTrainingDraft) {
          const data = { status: value === 'passed' };
          actionCompleteTraining(scormTrainingDraft.id, data);
        }
      }
    }
  }, [id, scormData, scormVersion, actionSaveScormContentInfo, actionCompleteTraining, scormTrainingDraft]);


  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);


  const updateScormData = (key, value) => {
    setScormData(prevScormData => ({
      ...prevScormData,
      [key]: value

    }));
  };

  useEffect(() => {
    const element = document.querySelector('body');
    if (element.classList.contains('modal-open')) {
      element.classList.remove('modal-open');
    }
  }, [loadingStatus]);

  useEffect(() => () => {
    setScormData({});
    setScormUrl('');
    setScormVersion('');
  }, []);

  useEffect(() => {
    if (trainingCompletion?.success === true) {
      setLoadingStatus(false);
      history.push(`${PORTAL}`);
    }
  }, [trainingCompletion, setLoadingStatus, history]);

  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage({
        type: 'INITIALIZE_SCORM',
        data: scormData
      }, '*');
    }
  }, [scormData]);


  return (
    <div className={styles.scormContainer}>
      <HeaderBack
        translationID="lms.training.back"
        translationDefault="Back"
        backRoute={PORTAL}
        customStyles={styles.backToDash}
      />
      <Loading loadingStatus={loadingStatus} />
      {!trainingPermission && (
        <div className={styles.content}>
          {fetchScormContetAction?.success && (
            <iframe
              ref={iframeRef}
              src={scormUrl}
              className={styles.scormContent}
              title="scorm"
              onLoad={handleIframeLoad}
            />
          )}
        </div>
      )}
      {trainingPermission && (
        <div className={styles.noData}>
          <span className={styles.noDataText}>
            <FormattedMessage
              id="documents.no_permission.message"
              defaultMessage="You do not have permission for portal training to perform any action."
            />
          </span>
        </div>
      )}
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  actionFetchTraining: (id) => {
    dispatch(fetchTrainingDetail(id));
  },
  actionFetchScormContent: (id) => {
    dispatch(getScormContent(id));
  },
  actionClearState: () => {
    dispatch(clearState());
  },
  actionFetchScormTrainingDraft: (id) => {
    dispatch(fetchScormTrainingInfo(id));
  },
  actionSaveScormContentInfo: (id, data) => {
    dispatch(saveScormContentInfo(id, data));
  },
  actionCompleteTraining: (id, data) => {
    dispatch(completePortalTraining(id, data));
  }
});

const mapStateToProps = state => ({
  trainingData: state.trainings.trainingDetail,
  trainingFetchAction: state.trainings.trainingFetchAction,
  scormContent: state.trainings.scormURL,
  trainingScormContentAction: state.trainings.getScormContentAction,
  scormTrainingDraft: state.trainings.scormTrainingDraft,
  fetchScormContetAction: state.trainings.fetchScormContetAction,
  completeTraining: state.trainings.actionCompleteTraining,
  trainingCompletion: state.trainings.trainingCompletion,
  trainingPermission: state.trainings.errorMess
});

ScormTrainingContent.propTypes = {
  actionFetchTraining: PropTypes.func.isRequired,
  actionFetchScormContent: PropTypes.func.isRequired,
  actionClearState: PropTypes.func.isRequired,
  scormContent: PropTypes.shape({
    url: PropTypes.string
  }),
  trainingScormContentAction: PropTypes.shape({
    loading: PropTypes.bool,
    success: PropTypes.bool
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      trainingId: PropTypes.string
    })
  }).isRequired
};

ScormTrainingContent.defaultProps = {
  scormContent: { url: '' },
  trainingScormContentAction: {
    loading: false,
    success: false
  }
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(ScormTrainingContent));
